import type {
  FindOperationsInput,
  OperationItem,
} from '@ops/entities/operation-item';
import type { SelectOption } from '@/shared/ui';
import type { OpsDashboardSearch } from '../model/search-params';
import { useAgentPanelStore } from '@agent-panel/index';
import { UTCDate } from '@date-fns/utc';
import {
  useOperationItemsData,
  useOperationStatusesData,
  useUpdateAttentionCategory,
  useUpdateStatus,
} from '@ops/entities/operation-item';
import {
  DASHBOARD_V2_CARDS,
  DEFAULT_FILTER_CARD_ID,
  FilterCardsBar,
  getDashboardFilterCard,
  overlayCardFilter,
  timeWindowDates,
} from '@ops/features/filter-cards';
import { SupplierChangeDialog } from '@ops/features/supplier-change';
import { toastMutation } from '@ops/shared/lib/toast-mutation';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { ATTENTION_RED_FLAGGED } from '@travel-planner/shared/schemas/operations';
import { useMemo, useState } from 'react';
import { Select } from '@/shared/ui';
import { useDashboardSelection } from '../model/use-dashboard-selection';
import { BulkAssignOperatorDialog } from './bulk-assign-operator-dialog';
import { DashboardHeader } from './dashboard-header';
import { DashboardToolbar } from './dashboard-toolbar';
import { ItemsTable } from './items-table';
import css from './operations-dashboard.module.scss';
import { RecentChangesPanel } from './recent-changes-panel';

const DEFAULT_LIMIT = 20;
// List sort control (design: "Sort: Date"). Maps to operations sortBy fields.
const SORT_OPTIONS: SelectOption[] = [
  { label: 'Sort: Date', value: 'date' },
  { label: 'Sort: Service', value: 'product' },
  { label: 'Sort: Status', value: 'status' },
];
// Operation status code that means "awaiting confirmation" (the ops-dashboard
// MV classifies S4 = awaiting, S5 = confirmed, S91 = rejected).
const AWAITING_STATUS_CODE = 'S4';

export const OperationsDashboard = () => {
  const search = useSearch({ strict: false }) as OpsDashboardSearch;
  const navigate = useNavigate();

  // Stable "now" for the session so window / arriving-today filters don't churn.
  const now = useMemo(() => new UTCDate(), []);
  const window = search.window ?? 'today';
  const scope = search.scope ?? 'all';
  const cardId = search.card ?? DEFAULT_FILTER_CARD_ID;
  const offset = search.offset ?? 0;
  const limit = search.limit ?? DEFAULT_LIMIT;
  const customRange = {
    startDate: search.startDate,
    endDate: search.endDate,
  };

  const statusesQuery = useOperationStatusesData();
  const statusList = useMemo(
    () => statusesQuery.data ?? [],
    [statusesQuery.data]
  );
  const awaitingStatusIds = useMemo(
    () =>
      statusList
        .filter((s) => s.statusCode === AWAITING_STATUS_CODE)
        .map((s) => s.id),
    [statusList]
  );
  const statusColorById = useMemo(
    () => new Map(statusList.map((s) => [s.id, s.color] as const)),
    [statusList]
  );
  const statusOptions: SelectOption[] = useMemo(
    () => statusList.map((s) => ({ label: s.title ?? '—', value: s.id })),
    [statusList]
  );

  const { startDate, endDate } = timeWindowDates(window, now, customRange);

  // Base list query WITHOUT any card overlay — feeds the per-card counts. The
  // list itself overlays the selected card's filter on top (exact-count).
  const countBase: FindOperationsInput = {
    offset: 0,
    limit,
    view: 'active',
    startDate,
    endDate,
    search: search.search || undefined,
    searchField: search.searchField,
    sortBy: search.sortBy ?? 'date',
    sortOrder: search.sortOrder ?? 'asc',
    myItems: scope === 'mine' ? true : undefined,
  };

  const activeCard = getDashboardFilterCard(cardId);
  const cardFilter = activeCard?.filter({ now, awaitingStatusIds }) ?? {};
  // "All open" is the empty overlay (DEFAULT_FILTER_CARD_ID).
  const listInput: FindOperationsInput = {
    ...overlayCardFilter(countBase, cardFilter),
    offset,
  };

  const itemsQuery = useOperationItemsData(listInput, { keepPrevious: true });
  const selection = useDashboardSelection();
  const updateStatus = useUpdateStatus();
  const updateAttention = useUpdateAttentionCategory();

  const items = itemsQuery.data?.data ?? [];
  const total = itemsQuery.data?.total ?? 0;
  const selectedCardLabel = activeCard?.label ?? 'All open';
  const selectedIds = useMemo(
    () => [...selection.selected],
    [selection.selected]
  );

  // Bulk-action dialogs (supplier change + driver/guide) target the current
  // selection; captured at open time so the list can refetch underneath them.
  const [supplierDialogIds, setSupplierDialogIds] = useState<string[] | null>(
    null
  );
  const [operatorDialogOpen, setOperatorDialogOpen] = useState(false);

  const patch = (next: Partial<OpsDashboardSearch>) =>
    navigate({
      to: '.',
      replace: true,
      search: (prev) =>
        Object.fromEntries(
          Object.entries({ ...(prev as OpsDashboardSearch), ...next }).filter(
            ([, value]) => value !== undefined
          )
        ),
    });

  const handleToggleFlag = (item: OperationItem) => {
    if (!item.id) return;
    const isFlagged = item.attentionCategory === ATTENTION_RED_FLAGGED;
    updateAttention.mutate(
      {
        itineraryItemId: item.id,
        attentionCategory: isFlagged ? null : ATTENTION_RED_FLAGGED,
        attentionNote: item.attentionNote ?? null,
      },
      toastMutation({
        success: isFlagged ? 'Unflagged' : 'Flagged',
        error: 'Couldn’t update flag',
      })
    );
  };

  const handleBulkSetStatus = (operationStatusId: string) => {
    if (selectedIds.length === 0) return;
    updateStatus.mutate(
      { itineraryItemIds: selectedIds, operationStatusId },
      toastMutation(
        { success: 'Status updated', error: 'Couldn’t update status' },
        { onSuccess: selection.clear }
      )
    );
  };

  return (
    <div className={css.root}>
      <DashboardHeader />

      <DashboardToolbar
        customRange={customRange}
        onAsk={() => useAgentPanelStore.getState().open()}
        onCustomRangeChange={(range) =>
          patch({
            window: 'custom',
            startDate: range.startDate,
            endDate: range.endDate,
            offset: undefined,
          })
        }
        onScopeChange={(next) =>
          patch({ scope: next === 'all' ? undefined : next, offset: undefined })
        }
        onSearchChange={(value) =>
          patch({ search: value || undefined, offset: undefined })
        }
        onSelectWindow={(next) =>
          patch({
            window: next === 'today' ? undefined : next,
            startDate: next === 'custom' ? search.startDate : undefined,
            endDate: next === 'custom' ? search.endDate : undefined,
            offset: undefined,
          })
        }
        recentSlot={<RecentChangesPanel />}
        scope={scope}
        search={search.search ?? ''}
        window={window}
      />

      <FilterCardsBar
        activeCardId={cardId}
        awaitingStatusIds={awaitingStatusIds}
        baseInput={countBase}
        cards={DASHBOARD_V2_CARDS}
        now={now}
        onSelectCard={(id) =>
          patch({
            card: id === DEFAULT_FILTER_CARD_ID ? undefined : id,
            offset: undefined,
          })
        }
      />

      <div className={css.listToolbar}>
        <span className={css.context}>
          {selectedCardLabel} · {total.toLocaleString('en-US')} items
        </span>
        <Select
          onChange={(e) =>
            patch({
              sortBy:
                e.target.value === 'date'
                  ? undefined
                  : (e.target.value as OpsDashboardSearch['sortBy']),
            })
          }
          options={SORT_OPTIONS}
          size="sm"
          value={search.sortBy ?? 'date'}
        />
      </div>

      <ItemsTable
        isError={itemsQuery.isError}
        isLoading={itemsQuery.isLoading}
        items={items}
        limit={limit}
        offset={offset}
        onBulkAssignDriverGuide={() => setOperatorDialogOpen(true)}
        onBulkAssignSupplier={() => setSupplierDialogIds(selectedIds)}
        onBulkSetStatus={handleBulkSetStatus}
        onClearSelection={selection.clear}
        onOpenInOperations={(itineraryId) =>
          navigate({
            to: '/operations',
            search: { itineraries: [itineraryId] },
          })
        }
        onOpenItinerary={(itineraryId) =>
          navigate({ to: '/itineraries/$itineraryId', params: { itineraryId } })
        }
        onPageChange={(next) => patch({ offset: next || undefined })}
        onToggleAll={selection.setSelected}
        onToggleFlag={handleToggleFlag}
        onToggleSelect={selection.toggle}
        selectedIds={selection.selected}
        statusColorById={statusColorById}
        statusOptions={statusOptions}
        total={total}
      />

      {supplierDialogIds && (
        <SupplierChangeDialog
          initialSuppliers={[]}
          itineraryItemIds={supplierDialogIds}
          onClose={() => setSupplierDialogIds(null)}
          open
        />
      )}

      <BulkAssignOperatorDialog
        items={items}
        onClose={() => setOperatorDialogOpen(false)}
        onDone={selection.clear}
        open={operatorDialogOpen}
        selectedIds={selection.selected}
      />
    </div>
  );
};
